import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';

import {
  BRole,
  BTeam,
  BUser,
  NomenclaturesService,
  Scopes,
} from 'app/modules/data-model/data-model.module';
import { Config, newRoleIdConfig, newTeamIdConfig } from '../search-select/search-select.config';
import { MessageHandlerService } from '../message-handler/message-handler.service';
import { BAffiliation } from 'app/modules/data-model/user/affiliation.base';
import { AffiliationsRequest } from 'app/modules/data-model/auth-request/auth-request.service';

export type Affiliation = {
  team: BTeam;
  roles: BRole[];
};

export namespace Affiliation {
  export function fromBAffiliations(sources: BAffiliation[]): Affiliation[] {
    const result: Affiliation[] = [];
    for (const s of sources) {
      const targetAff = result.find((a) => a.team.code === s.team.code);
      if (targetAff) {
        targetAff.roles.push(s.role);
      } else {
        result.push({ team: s.team, roles: [s.role] });
      }
    }
    return result;
  }

  export function toAffiliationsRequest(sources: Affiliation[]): AffiliationsRequest {
    return sources.reduce((res, a) => {
      res[a.team.pk()] = a.roles.map((r) => r.pk());
      return res;
    }, {});
  }
}

type InternalAffiliation = {
  id: number;
  teamId: number;
  roleIds: number[];
  teamConfig: Config;
  roleConfig: Config;
};

@Component({
  selector: 'app-affiliation-picker',
  templateUrl: './affiliation-picker.component.html',
})
export class AffiliationPickerComponent implements OnInit, OnDestroy {
  @Input() user: BUser;
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() roleConfig: Config = newRoleIdConfig([], 'ROLES');
  @Input() teamConfig: Config = newTeamIdConfig([], 'TEAM');
  @Input() set selectedValues(vals: Affiliation[]) {
    this.affiliations = vals?.length
      ? this.toInternal(vals)
      : [
          {
            id: 1,
            teamId: undefined,
            roleIds: [],
            teamConfig: this.teamConfig,
            roleConfig: this.roleConfig,
          },
        ];
  }
  @Output() selectedValuesChange = new EventEmitter<Affiliation[]>();

  hasFullyDefinedAffiliation = false;
  affiliations: InternalAffiliation[] = [
    {
      id: 1,
      teamId: undefined,
      roleIds: [],
      teamConfig: this.teamConfig,
      roleConfig: this.roleConfig,
    },
  ];

  private sub: Subscription;
  private teams: BTeam[] = [];
  private roles: BRole[] = [];

  constructor(
    private nomenclaturesService: NomenclaturesService,
    private messageService: MessageHandlerService
  ) {}

  ngOnInit(): void {
    if (!this.roleConfig.items[0].options.length && !this.teamConfig.items[0].options.length) {
      this.sub = this.nomenclaturesService.get(new Scopes().teams().roles()).subscribe({
        next: (resp) => {
          this.teamConfig = newTeamIdConfig((this.teams = resp.teams), 'TEAM');
          this.roleConfig = newRoleIdConfig((this.roles = resp.roles), 'ROLES');
          this.affiliations
            .filter((intAff) => intAff.id === 1)
            .map((aff) => {
              aff.teamConfig = this.teamConfig;
              aff.roleConfig = this.roleConfig;
            });
        },
        error: (err) => this.messageService.httpError('Load Teams and Roles', err),
      });
    } else {
      this.teams = this.teamConfig.rawItems;
      this.roles = this.roleConfig.rawItems;
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  addAffiliation(): void {
    this.affiliations.push({
      id: new Date().getTime(),
      teamId: undefined,
      roleIds: [],
      teamConfig: this.teamConfig,
      roleConfig: this.roleConfig,
    });
    this.processAllTeamConfigs(this.affiliations);
  }

  removeAffiliation(id: number): void {
    this.affiliations.splice(
      this.affiliations.findIndex((a) => a.id === id),
      1
    );
    this.processAllTeamConfigs(this.affiliations);
    this.onChange();
  }

  onChange(): void {
    this.hasFullyDefinedAffiliation = this.affiliations.every((a) => a.teamId && a.roleIds?.length);
    this.selectedValuesChange.emit(this.fromInternal(this.affiliations));
  }

  private toInternal(affiliations: Affiliation[]): InternalAffiliation[] {
    let id = 1;
    let internalAffiliations = affiliations.map((a) => {
      const intAff = {
        id: id++,
        teamId: a.team?.pk(),
        roleIds: a.roles ? a.roles.map((r) => r.pk()) : [],
        teamConfig: this.teamConfig,
        roleConfig: this.roleConfig,
      };
      this.populateRoleConfig(intAff);
      return intAff;
    });

    this.processAllTeamConfigs(internalAffiliations);
    return internalAffiliations;
  }

  private returnFilteredRoles(intAff: InternalAffiliation): BRole[] {
    return this.roleConfig.rawItems.filter(
      (role) =>
        !this.user.affiliations.some(
          (aff) => aff.role.id === role.id && intAff.teamId === aff.team.pk()
        )
    );
  }

  private fromInternal(intAffArr: InternalAffiliation[]): Affiliation[] {
    return intAffArr.map((a) => {
      let filteredRoles = [];
      let selectedFilteredRoleIds = [];

      if (this.user && a.teamId) {
        filteredRoles = this.returnFilteredRoles(a);
        const filteredRoleIds = filteredRoles.map((role) => role.pk());
        selectedFilteredRoleIds = a.roleIds.filter((id) => filteredRoleIds.includes(id));
      }
      this.populateRoleConfig(a, filteredRoles);
      this.processAllTeamConfigs(intAffArr);

      const roleIds = filteredRoles.length > 0 ? selectedFilteredRoleIds : a.roleIds;
      return {
        team: this.teams.find((t) => t.pk() === a.teamId),
        roles: roleIds ? roleIds.map((rId) => this.roles.find((r) => r.pk() === rId)) : [],
      };
    });
  }

  private processAllTeamConfigs(intAffArr: InternalAffiliation[]): void {
    intAffArr.forEach((aff) => {
      const teamIdsExcludeCurrentOne = intAffArr
        .filter((a) => a.id !== aff.id)
        .map((a) => a.teamId);
      const teams = this.teamConfig.rawItems.filter(
        (team) => !teamIdsExcludeCurrentOne.includes(team.pk())
      );
      aff.teamConfig = newTeamIdConfig(teams, 'TEAM');
    });
  }

  private populateRoleConfig(intAff: InternalAffiliation, filteredRoles: BRole[] = []): void {
    if (this.user && intAff.teamId) {
      const roles = filteredRoles.length > 0 ? filteredRoles : this.returnFilteredRoles(intAff);
      intAff.roleConfig = newRoleIdConfig(roles, 'ROLES');
    } else {
      intAff.roleConfig = this.roleConfig;
    }
  }
}
